'use client';

import { Slider } from '@base-ui-components/react/slider';
import { ChevronDown } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { cn } from '../../../lib/utils';
import type { SanityImageProps } from '../../../sanity/utils/fragments/image-fragment';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../ui/accordion';
import { ColorSwatch } from '../../ui/color-swatch';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../ui/sheet';
import {
  type FilterValuesType,
  useProductListingFilters,
} from './use-product-listing-filters';

type ColorFilterOption = {
  _id: string;
  value: string;
  title: string;
  asset:
    | {
        type: 'image';
        image: SanityImageProps | null;
      }
    | {
        type: 'hex';
        color: string | null;
      };
};

type FinishFilterOption = {
  _id: string;
  value: string;
  title: string;
  asset: {
    type: 'image';
    image: SanityImageProps | null;
  };
};

type PatternFilterOption = {
  _id: string;
  value: string;
  title: string;
};

type PriceFilterOption = {
  min: number;
  max: number;
};

export type FilterOptions =
  | {
      type: 'color';
      options: ColorFilterOption[];
    }
  | {
      type: 'finish';
      options: FinishFilterOption[];
    }
  | {
      type: 'pattern';
      options: PatternFilterOption[];
    }
  | {
      type: 'price';
      priceRange: PriceFilterOption;
    };

export function ProductListingFilterSheet({
  filterOptions,
  closeButtonClassName,
  className,
  ...props
}: {
  filterOptions: FilterOptions[];
  closeButtonClassName: string;
} & Omit<ComponentPropsWithoutRef<typeof SheetContent>, 'children'>) {
  const [isPending, startTransition] = useTransition();
  const sheetContentRef = useRef<HTMLDivElement>(null);
  const { filterValues, setFilterValues } = useProductListingFilters({
    filterOptions,
    startTransition,
  });

  if (!filterOptions) return null;

  return (
    <Sheet>
      <SheetTrigger
        render={(props) => (
          <button
            type="button"
            className="flex items-center justify-center gap-2"
            {...props}
          >
            <span className="flex items-center justify-center gap-3 whitespace-nowrap text-sm">
              Show Filters
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-[0.85em] w-[0.85em]"
              >
                <title>Filter</title>
                <path
                  d="M14.126 9L3 9C2.44772 9 2 8.55228 2 8C2 7.44772 2.44772 7 3 7L14.126 7C14.5701 5.27477 16.1362 4 18 4C19.8638 4 21.4299 5.27477 21.874 7L21 7C21.5523 7 22 7.44772 22 8C22 8.55228 21.5523 9 21 9L21.874 9C21.4299 10.7252 19.8638 12 18 12C16.1362 12 14.5701 10.7252 14.126 9ZM16 8C16 6.89543 16.8954 6 18 6C19.1046 6 20 6.89543 20 8C20 9.10457 19.1046 10 18 10C16.8954 10 16 9.10457 16 8Z"
                  fill="currentColor"
                />
                <path
                  d="M9.87398 15L21 15C21.5523 15 22 15.4477 22 16C22 16.5523 21.5523 17 21 17L9.87398 17C9.42994 18.7252 7.86384 20 6 20C4.13616 20 2.57006 18.7252 2.12602 17L3 17C2.44772 17 2 16.5523 2 16C2 15.4477 2.44772 15 3 15L2.12602 15C2.57006 13.2748 4.13616 12 6 12C7.86384 12 9.42994 13.2748 9.87398 15ZM8 16C8 14.8954 7.10457 14 6 14C4.89543 14 4 14.8954 4 16C4 17.1046 4.89543 18 6 18C7.10457 18 8 17.1046 8 16Z"
                  fill="currentColor"
                />
              </svg>
            </span>
          </button>
        )}
      />
      <SheetContent
        side="left"
        className={cn('lg:max-w-[30rem]', className)}
        ref={sheetContentRef}
        {...props}
      >
        <SheetHeader>
          <SheetTitle className="font-medium text-[hsl(var(--color))] text-lg">
            Filters
          </SheetTitle>
        </SheetHeader>
        <SheetBody>
          <form className="max-w-full" id="product-filter-form">
            <Accordion
              openMultiple
              defaultValue={['availability', 'price', 'colors', 'finish']}
              className="w-full"
            >
              {filterOptions.map((filter) => {
                switch (filter.type) {
                  case 'color': {
                    if (!filter.options) return null;
                    return (
                      <ColorsFilter
                        key={filter.type}
                        colors={filter.options}
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        tooltipContainer={sheetContentRef}
                      />
                    );
                  }
                  case 'finish': {
                    if (!filter.options) return null;
                    return (
                      <FinishFilter
                        key={filter.type}
                        finishes={filter.options}
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                      />
                    );
                  }
                  case 'price': {
                    if (
                      !filter.priceRange ||
                      !filter.priceRange.min ||
                      !filter.priceRange.max
                    )
                      return null;
                    return (
                      <PriceFilter
                        key={filter.type}
                        range={filter.priceRange}
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                      />
                    );
                  }
                  case 'pattern': {
                    if (!filter.options) return null;
                    return (
                      <PatternFilter
                        key={filter.type}
                        patterns={filter.options}
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                      />
                    );
                  }
                  default:
                    return null;
                }
              })}
            </Accordion>
          </form>
        </SheetBody>

        <footer className="absolute inset-x-0 bottom-0 z-10 w-full border-t border-t-gray-200 bg-white px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] md:py-6">
          <div className="mt-3 flex w-full items-center">
            <SheetClose
              disabled={isPending}
              className={cn(closeButtonClassName)}
            >
              Apply
            </SheetClose>
          </div>
        </footer>
      </SheetContent>
    </Sheet>
  );
}

function FilterAccordionItem({
  value,
  title,
  children,
}: {
  value: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <AccordionItem value={value} className="w-full">
      <AccordionTrigger className="group flex w-full items-center justify-between border-gray-100 border-b py-4 text-left hover:text-[hsl(var(--color))]/80">
        <span>{title}</span>
        <ChevronDown className="size-4 origin-center text-current transition-all group-data-[panel-open]:rotate-180" />
      </AccordionTrigger>
      <AccordionContent>
        <div className="py-3">{children}</div>
      </AccordionContent>
    </AccordionItem>
  );
}

function PriceFilter({
  range,
  filterValues,
  setFilterValues,
}: {
  range: PriceFilterOption;
  filterValues: FilterValuesType;
  setFilterValues: (values: Partial<FilterValuesType>) => void;
}) {
  const [priceRange, setPriceRange] = useState([
    filterValues?.priceMin ? Number(filterValues.priceMin) : range.min,
    filterValues?.priceMax ? Number(filterValues.priceMax) : range.max,
  ]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const debouncedPriceUpdate = useCallback(
    (values: number[]) => {
      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setFilterValues({
          priceMin: String(values[0]),
          priceMax: String(values[1]),
        });
      }, 500);
    },
    [setFilterValues]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handlePriceChange = (values: number[]) => {
    setPriceRange(values);
    debouncedPriceUpdate(values);
  };

  return (
    <FilterAccordionItem value="price" title="Price">
      <div className="w-full">
        <input type="hidden" name="filter.v.price.gte" value={priceRange[0]} />
        <input type="hidden" name="filter.v.price.lte" value={priceRange[1]} />
        <div className="mt-4 flex justify-between font-medium text-[13px] tracking-tight">
          <span>${priceRange[0]}</span>
          <span>${priceRange[1]}</span>
        </div>
        <div className="relative mt-1 flex h-5 touch-none items-center px-2">
          <Slider.Root
            className="relative flex h-5 w-full touch-none items-center"
            value={priceRange}
            onValueChange={handlePriceChange}
            min={range.min}
            max={range.max}
            step={1}
            aria-label="Price Range"
          >
            <Slider.Control className="relative flex h-5 w-full touch-none items-center">
              <Slider.Track className="relative h-1 w-full rounded-full bg-gray-300">
                <Slider.Indicator className="absolute h-1 rounded-full bg-[hsl(var(--color))]" />
                <Slider.Thumb
                  className="block h-3 w-3 cursor-grab rounded-full border border-white bg-current focus:scale-150 focus:outline-none active:cursor-grabbing"
                  aria-label="Minimum price"
                />
                <Slider.Thumb
                  className="block h-3 w-3 cursor-grab rounded-full border border-white bg-current focus:scale-150 focus:outline-none active:cursor-grabbing"
                  aria-label="Maximum price"
                />
              </Slider.Track>
            </Slider.Control>
          </Slider.Root>
        </div>
      </div>
    </FilterAccordionItem>
  );
}

function ColorsFilter({
  colors,
  filterValues,
  setFilterValues,
  tooltipContainer,
}: {
  colors: ColorFilterOption[];
  filterValues: FilterValuesType;
  setFilterValues: (values: Partial<FilterValuesType>) => void;
  tooltipContainer?: RefObject<HTMLDivElement | null>;
}) {
  const selectedColors = (filterValues.color as string[]) || [];

  const handleColorChange = (colorValue: string) => {
    const isCurrentlySelected = selectedColors.includes(colorValue);
    const newColors = isCurrentlySelected
      ? selectedColors.filter((c: string) => c !== colorValue)
      : [...selectedColors, colorValue];

    setFilterValues({ color: newColors });
  };

  return (
    <FilterAccordionItem value="colors" title="Colours">
      <fieldset className="flex flex-wrap gap-2">
        {colors.map((color) => (
          <ColorSwatch
            key={color._id}
            hexColor={
              color.asset?.type === 'hex' ? color.asset.color : undefined
            }
            image={
              color.asset?.type === 'image' ? color.asset.image : undefined
            }
            label={color.title}
            isSelected={selectedColors.includes(color.value)}
            size="md"
            tooltipContainer={tooltipContainer}
            onClick={() => handleColorChange(color.value)}
          />
        ))}
      </fieldset>
    </FilterAccordionItem>
  );
}

function FinishFilter({
  finishes,
  filterValues,
  setFilterValues,
}: {
  finishes: FinishFilterOption[];
  filterValues: FilterValuesType;
  setFilterValues: (values: Partial<FilterValuesType>) => void;
}) {
  const selectedFinishes = (filterValues.finish as string[]) || [];

  const handleFinishChange = (finishValue: string, isChecked: boolean) => {
    const newFinishes = isChecked
      ? [...selectedFinishes, finishValue]
      : selectedFinishes.filter((f: string) => f !== finishValue);

    setFilterValues({ finish: newFinishes });
  };

  return (
    <FilterAccordionItem value="finish" title="Finish">
      <fieldset className="flex flex-wrap gap-2">
        {finishes.map((finish) => (
          <label
            key={finish._id}
            className={cn(
              'flex cursor-pointer flex-col items-center border px-5 py-2.5 hover:border-[hsl(var(--color-primary))]',
              filterValues.finish?.includes(finish.value) &&
                'border-[hsl(var(--color))]'
            )}
            title={finish.title}
          >
            <input
              type="checkbox"
              checked={selectedFinishes.includes(finish.value)}
              onChange={(e) =>
                handleFinishChange(finish.value, e.target.checked)
              }
              className="peer absolute appearance-none outline-none hover:outline-none focus:outline-none"
            />
            <span className="font-bold uppercase peer-checked:text-inherit">
              {finish.title}
            </span>
          </label>
        ))}
      </fieldset>
    </FilterAccordionItem>
  );
}

function PatternFilter({
  patterns,
  filterValues,
  setFilterValues,
}: {
  patterns: PatternFilterOption[];
  filterValues: FilterValuesType;
  setFilterValues: (values: Partial<FilterValuesType>) => void;
}) {
  const selectedPatterns = (filterValues.pattern as string[]) || [];

  const handlePatternChange = (patternValue: string, isChecked: boolean) => {
    const newPatterns = isChecked
      ? [...selectedPatterns, patternValue]
      : selectedPatterns.filter((p: string) => p !== patternValue);

    setFilterValues({ pattern: newPatterns });
  };

  return (
    <FilterAccordionItem value="pattern" title="Pattern">
      <fieldset className="flex flex-wrap gap-2">
        {patterns.map((pattern) => (
          <label
            key={pattern._id}
            className={cn(
              'flex cursor-pointer flex-col items-center border px-5 py-2.5 hover:border-[hsl(var(--color-primary))]',
              filterValues.pattern?.includes(pattern.value) &&
                'border-[hsl(var(--color))]'
            )}
            title={pattern.title}
          >
            <input
              type="checkbox"
              checked={selectedPatterns.includes(pattern.value)}
              onChange={(e) =>
                handlePatternChange(pattern.value, e.target.checked)
              }
              className="peer absolute appearance-none outline-none hover:outline-none focus:outline-none"
            />
            <span className="font-bold uppercase peer-checked:text-inherit">
              {pattern.title}
            </span>
          </label>
        ))}
      </fieldset>
    </FilterAccordionItem>
  );
}

// function AvailabilityFilter() {
//   return (
//     <FilterAccordionItem value="availability" title="Availability">
//       <fieldset className="grid gap-3">
//         <label
//           htmlFor="in-stock"
//           className="flex cursor-pointer items-center space-x-2 font-normal leading-[1.4]"
//         >
//           <span className="relative h-4 w-4">
//             <Checkbox.Root
//               className="h-full w-full appearance-none border border-black bg-white"
//               name="filter.v.availability"
//               value="1"
//               id="in-stock"
//             >
//               <Checkbox.Indicator className="flex h-full w-full items-center justify-center">
//                 <CheckIcon className="size-3.5 text-inherit" />
//               </Checkbox.Indicator>
//             </Checkbox.Root>
//           </span>
//           <span className="max-w-[220px] truncate">In stock</span>
//         </label>
//         <label
//           htmlFor="out-of-stock"
//           className="flex cursor-pointer items-center space-x-2 font-normal leading-[1.4]"
//         >
//           <span className="relative h-4 w-4">
//             <Checkbox.Root
//               className="h-full w-full appearance-none border border-black bg-white"
//               name="filter.v.availability"
//               value="0"
//               id="out-of-stock"
//             >
//               <Checkbox.Indicator className="flex h-full w-full items-center justify-center">
//                 <CheckIcon className="size-3.5 text-inherit" />
//               </Checkbox.Indicator>
//             </Checkbox.Root>
//           </span>
//           <span className="max-w-[220px] truncate">Out of stock</span>
//         </label>
//       </fieldset>
//     </FilterAccordionItem>
//   );
// }
